import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
	TASK_PRIORITY_LABELS,
	TASK_STATUS_COLORS,
	TASK_STATUS_OPTIONS,
} from "@/modules/tasks/constants";
import { useTasks } from "@/modules/tasks/hooks/useTasks";
import type { Task, TaskStatus } from "@/modules/tasks/types";
import { EmptyState } from "@/shared/components/EmptyState";
import { StatusTag } from "@/shared/components/StatusTag";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";

interface BrigadeTaskTableProps {
	organizationId: string;
	brigadeId: string;
}

/** Read-only list of tasks assigned to the brigade */
export function BrigadeTaskTable({ organizationId, brigadeId }: BrigadeTaskTableProps) {
	// Pagination lives in the URL (prefixed to avoid collisions with sibling tables)
	const { page, limit, setPage } = useUrlTableState({ prefix: "btask" });

	const { data, isLoading } = useTasks({ organizationId, brigadeId, page, limit });
	const items = data?.data?.items ?? [];
	const total = data?.data?.meta?.total ?? 0;

	const columns: ColumnsType<Task> = [
		{
			title: "№",
			key: "index",
			width: 55,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => formatTableIndex(page, limit, index),
		},
		{
			title: "Sarlavha",
			dataIndex: "title",
			key: "title",
			ellipsis: true,
		},
		{
			title: "Turi",
			key: "type",
			width: 130,
			render: (_: unknown, record: Task) => record.type?.name ?? record.typeId,
		},
		{
			title: "Ustuvorlik",
			dataIndex: "priority",
			key: "priority",
			width: 110,
			render: (p: string) => TASK_PRIORITY_LABELS[p as keyof typeof TASK_PRIORITY_LABELS] ?? p,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 130,
			render: (status: TaskStatus) => (
				<StatusTag tone={TASK_STATUS_COLORS[status]}>
					{TASK_STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status}
				</StatusTag>
			),
		},
		{
			title: "Tugash sanasi",
			dataIndex: "dueDate",
			key: "dueDate",
			width: 130,
			render: (date: string | null) =>
				date
					? new Date(date).toLocaleDateString("uz-UZ", {
							year: "numeric",
							month: "2-digit",
							day: "2-digit",
						})
					: "—",
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={items}
			loading={isLoading}
			rowKey="id"
			size="middle"
			pagination={tablePagination({
				page,
				limit,
				total,
				onChange: setPage,
				sizeChanger: false,
			})}
			locale={{
				emptyText: <EmptyState description="Brigadaga vazifa biriktirilmagan" />,
			}}
		/>
	);
}
